import {
  deleteSanitationForm,
  getSanitationForms,
  putSanitationForm,
} from "./db/sanitationForms.js";
import { checkOnlineStatus } from "./onlineStatus.js";
import { getFarmIdFromUrl } from "./utils.js";
import { reportIfQuotaError } from "./db/quotaSignal.js";

export async function syncOfflineSanitationForms({
  farmId = null,
  manifest = null,
  formId = null,
  onSyncStart = null,
} = {}) {
  const currentFarmId = farmId || getFarmIdFromUrl(window.location.pathname);
  if (!currentFarmId)
    throw new Error("Farm id is required to sync offline sanitation forms");

  const currentManifest =
    manifest || (await fetchOfflineManifest(currentFarmId, { formId }));
  const scopedManifest = formId
    ? manifestScopedToForm(currentManifest, formId)
    : currentManifest;
  const localForms = await getSanitationForms(currentFarmId);
  const planningLocalForms = formId
    ? localForms.filter((form) => sameId(form.id, formId))
    : localForms;
  const localDocuments =
    await getCachedOfflineDocumentsForManifest(scopedManifest);
  let plan = buildOfflineSanitationFormSyncPlan(
    planningLocalForms,
    localDocuments,
    scopedManifest,
  );
  let syncManifest = scopedManifest;

  if (plan.formsToCache.length > 0 || plan.documentsToFetch.length > 0) {
    if (onSyncStart) onSyncStart(plan);
    // Download documents only for the stale forms; fresh forms keep their
    // cached documents and are never re-rendered by the server.
    const staleFormIds = formId ? null : staleSyncPlanFormIds(plan);
    const documentsManifest = await manifestWithDocuments(
      currentFarmId,
      scopedManifest,
      {
        formId,
        formIds: staleFormIds,
      },
    );
    syncManifest = staleFormIds
      ? mergeManifestEntries(scopedManifest, documentsManifest)
      : documentsManifest;
    plan = buildOfflineSanitationFormSyncPlan(
      planningLocalForms,
      localDocuments,
      syncManifest,
    );
  }

  await Promise.all([
    ...(!formId
      ? plan.formsToDelete.map((form) => deleteSanitationForm(form.id))
      : []),
    ...plan.documentsToDelete.map((documentRecord) =>
      deleteCachedDocument(documentRecord.url),
    ),
  ]);

  await Promise.all(plan.formsToCache.map((form) => putSanitationForm(form)));

  const failedDocuments = [];
  for (const documentRecord of plan.documentsToFetch) {
    try {
      await cacheOfflineDocument(documentRecord);
    } catch (error) {
      failedDocuments.push({ documentRecord, error });
    }
  }

  if (failedDocuments.length > 0) {
    const error = new Error(
      "Failed to cache one or more offline sanitation form documents",
    );
    error.failedDocuments = failedDocuments;
    error.plan = plan;
    throw error;
  }

  try {
    await persistOfflineManifestSnapshot({
      farmId: currentFarmId,
      formId,
      baseManifest: manifest,
      syncManifest,
    });
  } catch (error) {
    // A failed snapshot write must not fail the sync itself; the documents
    // are already cached and offline readiness tolerates a stale manifest.
    console.warn(
      "Failed to persist offline sanitation manifest snapshot:",
      error,
    );
  }

  return {
    forms: await getSanitationForms(currentFarmId),
    plan,
    manifest: syncManifest,
  };
}

export async function ensureOfflineSanitationFormsReady({
  farmId = null,
  manifest = null,
  formId = null,
  online = null,
  onSyncStart = null,
  checkOnline = checkOnlineStatus,
  syncForms = syncOfflineSanitationForms,
  readCachedManifest = cachedOfflineManifest,
  getForms = getSanitationForms,
  putForm = putSanitationForm,
  deleteForm = deleteSanitationForm,
  getDocumentsForManifest = getCachedOfflineDocumentsForManifest,
} = {}) {
  const currentFarmId = Number(
    farmId || manifest?.farm_id || getFarmIdFromUrl(window.location.pathname),
  );
  if (!currentFarmId) {
    return { ready: false, forms: [], plan: null, source: "missing_farm" };
  }

  // Fast path: when the caller provides a manifest (the launcher embeds a
  // server-fresh one in the page) and nothing is stale locally, answer from
  // cache + IndexedDB alone — no online probe, no fetches, no downloads.
  if (manifestMatchesFarm(manifest, currentFarmId)) {
    const localReadiness = await selectedFormReadiness({
      farmId: currentFarmId,
      manifest,
      formId,
      requireFreshForms: true,
      getForms,
      getDocumentsForManifest,
    });

    if (
      localReadiness.ready &&
      localReadiness.plan.formsToDelete.length === 0
    ) {
      try {
        await persistOfflineManifestSnapshot({
          farmId: currentFarmId,
          formId,
          baseManifest: manifest,
          syncManifest: formId
            ? manifestScopedToForm(manifest, formId)
            : manifest,
        });
      } catch (error) {
        reportIfQuotaError(error);
        console.warn(
          "Failed to persist offline sanitation manifest snapshot:",
          error,
        );
      }

      return {
        ready: true,
        forms: await getForms(currentFarmId),
        plan: localReadiness.plan,
        source: "local",
      };
    }
  }

  const onlineStatus = online ?? (await checkOnline());
  if (onlineStatus !== false) {
    const result = await syncForms({
      farmId: currentFarmId,
      manifest,
      formId,
      onSyncStart,
    });
    if (formId && result.manifest) {
      const readiness = await selectedFormReadiness({
        farmId: currentFarmId,
        manifest: result.manifest,
        formId,
        forms: result.forms,
        requireFreshForms: true,
        getForms,
        getDocumentsForManifest,
      });

      return {
        ready: readiness.ready,
        forms: result.forms,
        plan: readiness.plan,
        source: "server",
      };
    }

    return {
      ready: formId
        ? result.forms.some((form) => sameId(form.id, formId))
        : true,
      forms: result.forms,
      plan: result.plan,
      source: "server",
    };
  }

  const cachedManifest = await readCachedManifest(currentFarmId);
  const currentManifest = cachedManifest || manifest;
  let localForms = await getForms(currentFarmId);

  if (!manifestMatchesFarm(currentManifest, currentFarmId)) {
    return {
      ready: false,
      forms: localForms,
      plan: null,
      source: "cache",
    };
  }

  // Offline we can't re-download anything, so readiness only requires every
  // manifest document to be present in the cache — a slightly stale document
  // is still fully usable. Version-driven refresh happens on the online path.
  const readiness = await selectedFormReadiness({
    farmId: currentFarmId,
    manifest: currentManifest,
    formId,
    requireFreshDocuments: false,
    getForms: async () => localForms,
    getDocumentsForManifest,
  });
  const plan = readiness.plan;
  const documentsReady = readiness.ready;

  if (!documentsReady) {
    return {
      ready: false,
      forms: localForms,
      plan,
      source: "cache",
    };
  }

  // Never delete cached documents offline: the manifest may be stale and a
  // version-mismatched document is the only usable copy until the next sync.
  await Promise.all([
    ...(!formId ? plan.formsToDelete.map((form) => deleteForm(form.id)) : []),
    ...plan.formsToCache.map((form) => putForm(form)),
  ]);

  localForms = await getForms(currentFarmId);

  return {
    ready: formId
      ? localForms.some(
          (form) =>
            sameId(form.id, formId) && sameId(form.farm_id, currentFarmId),
        )
      : true,
    forms: localForms,
    plan,
    source: "cache",
  };
}

export function buildOfflineSanitationFormSyncPlan(
  localForms,
  localDocuments,
  manifest,
) {
  const manifestForms = normalizeManifestForms(manifest);
  const manifestFormsById = new Map(
    manifestForms.map((form) => [String(form.id), form]),
  );
  const localFormsById = new Map(
    localForms.map((form) => [String(form.id), form]),
  );
  const manifestDocuments = manifestForms.flatMap(
    (form) => form.offline_documents,
  );
  const manifestDocumentsByUrl = new Map(
    manifestDocuments.map((documentRecord) => [
      documentRecord.url,
      documentRecord,
    ]),
  );
  const localDocumentsByUrl = new Map(
    localDocuments.map((documentRecord) => [
      documentRecord.url,
      documentRecord,
    ]),
  );

  const formsToDelete = [];
  const retainedLocalForms = [];
  for (const localForm of localForms) {
    if (manifestFormsById.has(String(localForm.id))) {
      retainedLocalForms.push(localForm);
    } else {
      formsToDelete.push(localForm);
    }
  }

  const formsToCache = manifestForms.filter((manifestForm) => {
    const localForm = localFormsById.get(String(manifestForm.id));
    return !formIsFresh(localForm, manifestForm);
  });

  const documentsToDelete = localDocuments.filter((localDocument) => {
    const manifestDocument = manifestDocumentsByUrl.get(localDocument.url);
    return (
      !manifestDocument ||
      String(localDocument.version) !== String(manifestDocument.version)
    );
  });

  const documentsToFetch = manifestDocuments.filter((manifestDocument) => {
    const localDocument = localDocumentsByUrl.get(manifestDocument.url);
    return (
      !localDocument ||
      String(localDocument.version) !== String(manifestDocument.version)
    );
  });

  const missingDocuments = manifestDocuments.filter(
    (manifestDocument) => !localDocumentsByUrl.has(manifestDocument.url),
  );

  return {
    formsToCache,
    formsToDelete,
    retainedLocalForms,
    documentsToDelete,
    documentsToFetch,
    missingDocuments,
    manifestForms,
  };
}

export function formIsFresh(localForm, manifestForm) {
  if (!localForm) return false;

  const localVersion = localForm.offline_version || localForm.updated_at;
  const manifestVersion =
    manifestForm.offline_version || manifestForm.updated_at;
  if (!localVersion || !manifestVersion) return false;

  if (
    !localForm.offline_version &&
    (!manifestForm.offline_version ||
      manifestForm.offline_version === manifestForm.updated_at)
  ) {
    if (new Date(localVersion) < new Date(manifestVersion)) return false;
  } else if (String(localVersion) !== String(manifestVersion)) {
    return false;
  }

  return documentsAreFresh(
    localForm.offline_documents || [],
    manifestForm.offline_documents || [],
  );
}

function documentsAreFresh(localDocuments, manifestDocuments) {
  if (localDocuments.length !== manifestDocuments.length) return false;

  const localDocumentsByUrl = new Map(
    localDocuments.map((documentRecord) => [
      documentRecord.url,
      documentRecord,
    ]),
  );

  return manifestDocuments.every((manifestDocument) => {
    const localDocument = localDocumentsByUrl.get(manifestDocument.url);
    return (
      localDocument &&
      String(localDocument.version) === String(manifestDocument.version)
    );
  });
}

function offlineManifestUrl(farmId) {
  return `/farms/${farmId}/route_launcher/offline_manifest.json`;
}

async function fetchOfflineManifest(
  farmId,
  { includeDocuments = false, formId = null, formIds = null } = {},
) {
  const params = new URLSearchParams();
  if (includeDocuments) params.set("include_documents", "true");
  if (formId) params.set("form_id", formId);
  if (formIds?.length) params.set("form_ids", formIds.join(","));
  const query = params.toString() ? `?${params}` : "";
  const response = await fetch(`${offlineManifestUrl(farmId)}${query}`, {
    cache: "no-cache",
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch offline sanitation form manifest for farm ${farmId}`,
    );
  }

  return await response.json();
}

async function cachedOfflineManifest(farmId) {
  if (typeof caches === "undefined") return null;

  const cache = await caches.open("documents");
  const response = await cache.match(offlineManifestUrl(farmId), {
    ignoreSearch: false,
    ignoreVary: true,
  });
  if (!response) return null;

  return await response.json();
}

// Keeps the cached manifest in lockstep with the documents that were just
// cached. Offline readiness checks compare cached documents against this
// manifest, so it must be written by the same sync that wrote the documents —
// otherwise a stale manifest reports fully-downloaded forms as incomplete.
async function persistOfflineManifestSnapshot({
  farmId,
  formId,
  baseManifest,
  syncManifest,
}) {
  if (typeof caches === "undefined") return;

  let snapshot = syncManifest;
  if (formId) {
    // A form-scoped manifest must never be persisted as the farm manifest:
    // merge it into the latest full manifest instead, or skip when none exists.
    const fullManifest = (await cachedOfflineManifest(farmId)) || baseManifest;
    if (!fullManifest) return;

    snapshot = mergeManifestEntries(fullManifest, syncManifest);
  }

  const cache = await caches.open("documents");
  await cache.put(
    offlineManifestUrl(farmId),
    new Response(JSON.stringify(stripManifestDocumentHtml(snapshot)), {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Date: new Date().toUTCString(),
      },
    }),
  );
}

function mergeManifestEntries(fullManifest, scopedManifest) {
  const scopedEntries = scopedManifest.sanitation_forms || [];
  const scopedIds = new Set(
    scopedEntries.map((entry) => String(manifestEntryFormId(entry))),
  );

  return {
    ...fullManifest,
    generated_at: scopedManifest.generated_at || fullManifest.generated_at,
    sanitation_forms: [
      ...(fullManifest.sanitation_forms || []).filter(
        (entry) => !scopedIds.has(String(manifestEntryFormId(entry))),
      ),
      ...scopedEntries,
    ],
  };
}

function manifestEntryFormId(entry) {
  return entry.id || entry.sanitation_form?.id || entry.metadata?.id;
}

function stripManifestDocumentHtml(manifest) {
  return {
    ...manifest,
    sanitation_forms: (manifest.sanitation_forms || []).map((entry) => ({
      ...entry,
      urls: (entry.urls || []).map(
        ({ html, ...documentRecord }) => documentRecord,
      ),
    })),
  };
}

async function manifestWithDocuments(
  farmId,
  manifest,
  { formId = null, formIds = null } = {},
) {
  if (manifestHasDocuments(manifest)) return manifest;

  return await fetchOfflineManifest(farmId, {
    includeDocuments: true,
    formId,
    formIds,
  });
}

// Unique ids of the forms a sync plan still needs to download.
export function syncPlanStaleFormIds(plan) {
  return new Set([
    ...plan.formsToCache.map((form) => String(form.id)),
    ...plan.documentsToFetch.map((documentRecord) =>
      String(documentRecord.sanitation_form_id),
    ),
  ]);
}

// Form ids needing document downloads, or null when every manifest form is
// stale (a scoped request would not be smaller than the full one).
function staleSyncPlanFormIds(plan) {
  const staleIds = syncPlanStaleFormIds(plan);

  return staleIds.size < plan.manifestForms.length ? [...staleIds] : null;
}

export function manifestHasDocuments(manifest) {
  if (!manifest) return false;

  return (manifest?.sanitation_forms || []).every((form) =>
    (form.urls || []).every((documentRecord) => documentRecord.html),
  );
}

async function cacheOfflineDocument(documentRecord) {
  if (!documentRecord.html) {
    throw new Error(`Missing offline HTML for ${documentRecord.url}`);
  }

  const cache = await caches.open("documents");
  await cache.put(
    documentRecord.url,
    new Response(documentRecord.html, {
      headers: {
        "Content-Type": "text/html; charset=utf-8",
        Date: new Date().toUTCString(),
        "X-Artemis-Offline-Version": String(documentRecord.version),
      },
    }),
  );
}

async function getCachedOfflineDocuments(requiredDocuments) {
  const cache = await caches.open("documents");
  const documents = [];

  for (const documentRecord of requiredDocuments) {
    const response = await cache.match(documentRecord.url);
    if (!response) continue;

    documents.push({
      url: documentRecord.url,
      farm_id: documentRecord.farm_id,
      sanitation_form_id: documentRecord.sanitation_form_id,
      version: response.headers.get("X-Artemis-Offline-Version"),
    });
  }

  return documents;
}

async function getCachedOfflineDocumentsForManifest(manifest) {
  const manifestForms = normalizeManifestForms(manifest);
  const requiredDocuments = manifestForms.flatMap(
    (form) => form.offline_documents,
  );

  return await getCachedOfflineDocuments(requiredDocuments);
}

async function deleteCachedDocument(url) {
  const cache = await caches.open("documents");
  await cache.delete(url);
}

function normalizeManifestForms(manifest) {
  return (manifest.sanitation_forms || []).map((entry) => {
    const metadata = normalizeMetadata(
      entry.sanitation_form || entry.metadata || entry,
    );
    const documents = (entry.urls || entry.documents || []).map(
      (documentRecord) => ({
        ...documentRecord,
        farm_id: Number(entry.farm_id || metadata.farm_id || manifest.farm_id),
        sanitation_form_id: Number(entry.id || metadata.id),
      }),
    );

    return {
      ...metadata,
      id: Number(entry.id || metadata.id),
      farm_id: Number(entry.farm_id || metadata.farm_id || manifest.farm_id),
      offline_version:
        entry.version || metadata.offline_version || metadata.updated_at,
      offline_documents: documents,
    };
  });
}

function normalizeMetadata(metadata) {
  if (typeof metadata === "string") return JSON.parse(metadata);

  return metadata;
}

function manifestMatchesFarm(manifest, farmId) {
  return manifest && sameId(manifest.farm_id, farmId);
}

function manifestScopedToForm(manifest, formId) {
  return {
    ...manifest,
    sanitation_forms: (manifest.sanitation_forms || []).filter((entry) =>
      sameId(manifestEntryFormId(entry), formId),
    ),
  };
}

function sameId(left, right) {
  return Number(left) === Number(right);
}

async function selectedFormReadiness({
  farmId,
  manifest,
  formId = null,
  forms = null,
  requireFreshForms = false,
  requireFreshDocuments = true,
  getForms,
  getDocumentsForManifest,
}) {
  const readinessManifest = formId
    ? manifestScopedToForm(manifest, formId)
    : manifest;
  const currentForms = forms || (await getForms(farmId));
  const planningForms = formId
    ? currentForms.filter((form) => sameId(form.id, formId))
    : currentForms;
  const localDocuments = await getDocumentsForManifest(readinessManifest);
  const plan = buildOfflineSanitationFormSyncPlan(
    planningForms,
    localDocuments,
    readinessManifest,
  );
  const manifestHasRequestedForm =
    !formId || plan.manifestForms.some((form) => sameId(form.id, formId));
  const documentsReady = requireFreshDocuments
    ? plan.documentsToFetch.length === 0 && plan.documentsToDelete.length === 0
    : plan.missingDocuments.length === 0;

  return {
    ready:
      manifestHasRequestedForm &&
      plan.manifestForms.length > 0 &&
      (!requireFreshForms || plan.formsToCache.length === 0) &&
      documentsReady,
    plan,
  };
}
